import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DjayAvatar, DjayEmotion } from '../../mascot';
import { getProgressToNextLevel, APP_NAME, PORTAL_LABELS } from '../../config/constants';
import { getLearnerNavItems } from './navigationHelpers';
import { dataLayer } from '../../data/dataLayer';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, useVariantUnlocks, TOTAL_VARIANTS } from '../../mascot';
import { StreakFlame } from '../shared/StreakFlame';
import { soundManager } from '../../utils/soundManager';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { useAchievementCount } from '../shared/useAchievementCount';
interface SidebarProps {
  currentView: string;
  onNavigate: (view: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}
export function Sidebar({
  currentView,
  onNavigate,
  isCollapsed,
  onToggleCollapse
}: SidebarProps) {
  // Pull required state from the global store
  const {
    state,
    currentLearnerDisplayName,
    viewingLearnerId,
    accounts
  } = useAppStore();
  // Derive values from store
  const stats = state.stats;
  const displayName = currentLearnerDisplayName;
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts?.accountsById?.[activeAccountId];
  const activeAccountRole = viewingLearnerId ? 'learner' : activeAccount?.role || 'learner';
  const isLearner = activeAccountRole === 'learner';
  const isTeacherOrParent = activeAccountRole === 'teacher' || activeAccountRole === 'parent';
  // Avatar handling for learners
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  useEffect(() => {
    if (isLearner) {
      const loadAvatar = async () => {
        const prefs = await dataLayer.getLearnerPreferences();
        if (prefs?.avatarUrl) {
          setAvatarUrl(prefs.avatarUrl);
        }
      };
      loadAvatar();
    }
  }, [isLearner, accounts?.activeAccountId]);
  useEffect(() => {
    if (isLearner && activeAccountId) {
      dataLayer.getDjayLongPressDiscovered(activeAccountId).then(setHasDiscoveredLongPress).catch(() => {});
    }
  }, [isLearner, activeAccountId]);
  const {
    selectedVariant
  } = useDjaySelection();
  // Use shared variant unlock hook
  const {
    unlockedCount
  } = useVariantUnlocks(hasDiscoveredLongPress);
  // Use shared achievement count hook (single source of truth)
  const unlockedAchievementsCount = useAchievementCount();
  const [hasActiveAssignments, setHasActiveAssignments] = useState(false);
  useEffect(() => {
    if (isLearner && activeAccountId) {
      const checkAssignments = async () => {
        const [assessments, practice] = await Promise.all([dataLayer.getTeacherAssignments(), dataLayer.getPracticeAssignments()]);
        const now = new Date();
        const hasValidAssessments = Object.values(assessments).some(a => {
          if (a.deadline && new Date(a.deadline) < now) return false;
          if (a.completedByStudentIds?.includes(activeAccountId)) return false;
          return a.wholeClass || a.targetStudentIds?.includes(activeAccountId);
        });
        const hasValidPractice = Object.values(practice).some(a => {
          if (a.deadline && new Date(a.deadline) < now) return false;
          if (a.completedByStudentIds?.includes(activeAccountId)) return false;
          return a.wholeClass || a.targetStudentIds?.includes(activeAccountId);
        });
        setHasActiveAssignments(hasValidAssessments || hasValidPractice);
      };
      checkAssignments();
    }
  }, [isLearner, activeAccountId]);
  // Calculate XP progress for the profile section
  const progressToNextLevel = stats?.xp != null ? getProgressToNextLevel(stats.xp).percent : 0;
  // Streak from account state
  const streak = activeAccount?.appState?.streak?.currentTotalStreak || 0;
  // Contextual Djay emotion
  let contextEmotion: DjayEmotion = 'happy';
  const hour = new Date().getHours();
  if (hour < 8 || hour > 20) contextEmotion = 'sleeping';else if (streak >= 5) contextEmotion = 'excited';else if (streak === 0) contextEmotion = 'encouraging';
  return <motion.aside className="hidden md:flex flex-col h-full bg-white border-r-2 border-slate-100 sticky top-0 z-30 shadow-[4px_0_24px_rgba(249,115,22,0.03)]" initial={false} animate={{
    width: isCollapsed ? 88 : 280
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} data-id="element-1533">
      {/* Logo Section - Playful */}
      <div className="h-24 flex items-center px-7 relative overflow-hidden" data-id="element-1534">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none" data-id="element-1535"></div>

        {/* @djay-slot: sidebar-logo */}
        <motion.div className="flex-shrink-0 relative z-10" whileHover={{
        rotate: 10,
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} data-id="element-1536">
          <DjayAvatar size="lg" emotion={contextEmotion} variant={selectedVariant} data-id="element-1537" />
        </motion.div>

        <AnimatePresence data-id="element-1538">
          {!isCollapsed && <motion.span initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} className="font-black text-slate-800 ml-4 text-2xl tracking-tight whitespace-nowrap overflow-hidden relative z-10" data-id="element-1539">
              {APP_NAME}
            </motion.span>}
        </AnimatePresence>
      </div>

      {/* Navigation Items - Chunky & Colorful */}
      <nav className="flex-1 px-4 space-y-2 mt-4" data-id="element-1540">
        {getLearnerNavItems().map(item => {
        const isActive = currentView === item.id;
        const Icon = item.icon;
        return <motion.button key={item.id} onClick={() => {
          soundManager.playNavigate();
          onNavigate(item.id);
        }} className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-xl
                transition-colors group relative
                ${isActive ? 'text-orange-700' : 'text-slate-600 hover:bg-slate-100/80'}
              `} whileHover={{
          x: isCollapsed ? 0 : 4
        }} whileTap={{
          scale: 0.98
        }} data-id="element-1541">
              {isActive && <motion.div layoutId="activeNavIndicator" className="absolute inset-0 bg-orange-100 rounded-xl border border-orange-200 shadow-sm" initial={false} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-1542" />}
              <div className="relative flex-shrink-0 z-10" data-id="element-1543">
                <Icon className={`w-6 h-6 transition-transform duration-300 ${isActive ? 'animate-wiggle text-orange-600' : 'group-hover:scale-110'}`} strokeWidth={isActive ? 2.5 : 2} data-id="element-1544" />
                {item.id === 'assessment' && hasActiveAssignments && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 border-2 border-white rounded-full" data-id="element-1545" />}
              </div>
              <AnimatePresence data-id="element-1546">
                {!isCollapsed && <motion.span initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -10
            }} className={`ml-4 whitespace-nowrap overflow-hidden relative z-10 text-base ${isActive ? 'font-black' : 'font-bold'}`} data-id="element-1547">
                    {item.label}
                  </motion.span>}
              </AnimatePresence>
            </motion.button>;
      })}
      </nav>

      {/* Mijn Collectie Button */}
      {isLearner && <div className="px-4 mt-2 mb-2" data-id="element-1548">
          <motion.button onClick={() => {
        soundManager.playNavigate();
        onNavigate('collection');
      }} className={`
              w-full flex items-center gap-3 p-2 rounded-2xl border-2 transition-all group relative overflow-hidden
              ${currentView === 'collection' ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-400 ring-offset-1' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-orange-300 hover:shadow-md'}
            `} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} data-id="element-1549">
            <div className="relative flex-shrink-0" data-id="element-1550">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-1551">
                <DjayAvatar size="sm" emotion="excited" variant={selectedVariant} data-id="element-1552" />
              </motion.div>
            </div>

            <AnimatePresence data-id="element-1553">
              {!isCollapsed && <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} className="flex-1 text-left overflow-hidden" data-id="element-1554">
                  <span className="block font-black text-orange-900 text-sm truncate" data-id="element-1555">
                    Mijn Collectie
                  </span>
                  <span className="block text-[9px] font-bold text-orange-600 truncate" data-id="element-1556">
                    {unlockedCount}/{TOTAL_VARIANTS} Varianten •{' '}
                    {unlockedAchievementsCount}/{ACHIEVEMENTS.length}{' '}
                    Achievements
                  </span>
                </motion.div>}
            </AnimatePresence>
          </motion.button>
        </div>}

      {/* Learner Profile Section */}
      {isLearner && !isCollapsed && stats && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1557">
          <div className="px-4 py-3" data-id="element-1558">
            {/* Avatar and Name */}
            <div className="flex items-center gap-3 mb-3" data-id="element-1559">
              {/* Avatar with custom image support */}
              {avatarUrl ? <img src={avatarUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-id="element-1560" /> : <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1561">
                  {displayName.charAt(0).toUpperCase()}
                </div>}
              <div className="flex-1 min-w-0" data-id="element-1562">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1563">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1564">Level {stats.level}</p>
              </div>

              {/* Streak Flame */}
              <div className="flex-shrink-0" data-id="element-1565">
                <StreakFlame streak={streak} size="sm" data-id="element-1566" />
              </div>
            </div>

            {/* XP Progress */}
            <div className="space-y-1" data-id="element-1567">
              <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-1568">
                <span data-id="element-1569">XP</span>
                <span data-id="element-1570">
                  {stats.xp} / {getProgressToNextLevel(stats.xp).nextLevelXP}
                </span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1571">
                <motion.div className="h-full bg-gradient-to-r from-purple-500 to-blue-500" initial={{
              width: 0
            }} animate={{
              width: `${progressToNextLevel}%`
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} data-id="element-1572" />
              </div>
            </div>
          </div>
        </div>}

      {/* Teacher/Parent Profile Section */}
      {isTeacherOrParent && !isCollapsed && <div className="mt-auto pt-4 border-t border-gray-200" data-id="element-1573">
          <div className="px-4 py-3" data-id="element-1574">
            <div className="flex items-center gap-3 mb-3" data-id="element-1575">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-1576">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1577">
                <p className="font-semibold text-gray-900 truncate" data-id="element-1578">
                  {displayName}
                </p>
                <p className="text-xs text-gray-500" data-id="element-1579">
                  {PORTAL_LABELS[activeAccountRole] ?? activeAccountRole}
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Collapse Toggle */}
      <button onClick={onToggleCollapse} className="absolute -right-3.5 top-28 w-7 h-7 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center shadow-sm text-slate-400 hover:text-orange-500 hover:border-orange-300 hover:scale-110 transition-all z-40 focus:outline-none" aria-label={isCollapsed ? 'Uitklappen' : 'Inklappen'} data-id="element-1580">
        {isCollapsed ? <ChevronRight className="w-4 h-4 stroke-[3]" data-id="element-1581" /> : <ChevronLeft className="w-4 h-4 stroke-[3]" data-id="element-1582" />}
      </button>
    </motion.aside>;
}